import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Text,
  Avatar,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Input,
  Select,
  Flex,
  Divider,
  Icon,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/ApiContext';
import type { CordeCommonModelsDtoTeamTeam } from '@/api/generated/v1/api';

// Icons
const SearchIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
    />
  </Icon>
);

const MoreIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"
    />
  </Icon>
);

const TeamIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={6}>
    <path
      fill="currentColor"
      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
    />
  </Icon>
);

interface TeamsListProps {
  listType: 'all' | 'member_of' | 'managed';
  showSearch?: boolean;
  showFilters?: boolean;
  title?: string;
  emptyMessage?: string;
  emptyDescription?: string;
}

interface Filters {
  search: string;
  teamType: string;
  status: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

export const TeamsList: React.FC<TeamsListProps> = ({
  listType,
  showSearch = true,
  showFilters = true,
  title,
  emptyMessage,
  emptyDescription,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { TeamApi } = useApi();
  
  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // State
  const [teams, setTeams] = useState<CordeCommonModelsDtoTeamTeam[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(12);
  
  // Filters
  const [filters, setFilters] = useState<Filters>({
    search: '',
    teamType: '',
    status: '',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Load teams
  const loadTeams = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await TeamApi.listTeams(
        undefined, // xUserProfileID
        filters.teamType || undefined,
        filters.status || undefined,
        listType,
        filters.sortBy,
        filters.sortOrder,
        pageSize,
        page * pageSize
      );

      const data = (response as any)?.data || response;
      setTeams(data?.teams || []);
      setTotal(data?.pagination?.total_count || 0);
    } catch (err: any) {
      console.error('Error loading teams:', err);
      setError(err.message || t('teams.failedToLoadTeams'));
      toast({
        title: t('common.error'),
        description: err.message || t('teams.failedToLoadTeams'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Search teams
  const searchTeams = async () => {
    if (!filters.search.trim()) {
      loadTeams();
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const response = await TeamApi.searchTeams({
        query: filters.search,
        team_type: filters.teamType || undefined,
        status: filters.status || undefined,
        list_type: listType,
        sort_by: filters.sortBy,
        sort_order: filters.sortOrder,
        limit: pageSize,
        offset: page * pageSize,
      });

      const data = (response as any)?.data || response;
      setTeams(data?.teams || []);
      setTotal(data?.pagination?.total_count || 0);
    } catch (err: any) {
      console.error('Error searching teams:', err);
      setError(err.message || t('teams.failedToSearchTeams'));
      toast({
        title: t('common.error'),
        description: err.message || t('teams.failedToSearchTeams'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0); // Reset to first page
  };

  // Handle team click
  const handleTeamClick = (team: CordeCommonModelsDtoTeamTeam) => {
    navigate(`/teams/${team.id}`);
  };

  // Get team type badge color
  const getTeamTypeBadgeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'project':
        return 'blue';
      case 'business':
        return 'green';
      case 'social':
        return 'purple';
      case 'gaming':
        return 'orange';
      case 'educational':
        return 'yellow';
      case 'nonprofit':
        return 'teal';
      default:
        return 'gray';
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'yellow';
      case 'archived':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Get avatar URL
  const getAvatarUrl = (team: CordeCommonModelsDtoTeamTeam) => {
    if (team.avatar_url) {
      return team.avatar_url.startsWith('http') 
        ? team.avatar_url 
        : `/api/v1/teams/${team.id}/avatar`;
    }
    return undefined;
  };

  // Effects
  useEffect(() => {
    if (filters.search.trim()) {
      const timeoutId = setTimeout(searchTeams, 500);
      return () => clearTimeout(timeoutId);
    } else {
      loadTeams();
    }
  }, [filters, page, listType]);

  // Loading state
  if (isLoading && teams.length === 0) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" />
        <Text>{t('teams.loadingTeams')}</Text>
      </VStack>
    );
  }

  // Error state
  if (error && teams.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
        <Button onClick={loadTeams} colorScheme="primary">
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header with title and search */}
      {(title || showSearch) && (
        <VStack spacing={4} align="stretch">
          {title && (
            <Text fontSize="xl" fontWeight="semibold" color="text.primary">
              {title}
            </Text>
          )}

          {showSearch && (
            <HStack spacing={4}>
              <Box flex={1}>
                <Input
                  placeholder={t('teams.searchPlaceholder')}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  leftElement={<SearchIcon />}
                />
              </Box>
              {showFilters && (
                <HStack spacing={2}>
                  <Select
                    placeholder={t('teams.filterByType')}
                    value={filters.teamType}
                    onChange={(e) => handleFilterChange('teamType', e.target.value)}
                    w="150px"
                  >
                    <option value="project">{t('teams.teamTypeProject')}</option>
                    <option value="business">{t('teams.teamTypeBusiness')}</option>
                    <option value="social">{t('teams.teamTypeSocial')}</option>
                    <option value="gaming">{t('teams.teamTypeGaming')}</option>
                    <option value="educational">{t('teams.teamTypeEducational')}</option>
                    <option value="nonprofit">{t('teams.teamTypeNonprofit')}</option>
                  </Select>

                  <Select
                    placeholder={t('teams.filterByStatus')}
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    w="120px"
                  >
                    <option value="active">{t('teams.statusActive')}</option>
                    <option value="inactive">{t('teams.statusInactive')}</option>
                    <option value="archived">{t('teams.statusArchived')}</option>
                  </Select>

                  <Select
                    value={`${filters.sortBy}-${filters.sortOrder}`}
                    onChange={(e) => {
                      const [sortBy, sortOrder] = e.target.value.split('-') as [string, 'asc' | 'desc'];
                      setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                    }}
                    w="140px"
                  >
                    <option value="name-asc">{t('teams.sortByName')} ↑</option>
                    <option value="name-desc">{t('teams.sortByName')} ↓</option>
                    <option value="created_at-desc">{t('teams.sortByCreated')} ↓</option>
                    <option value="created_at-asc">{t('teams.sortByCreated')} ↑</option>
                    <option value="last_visited-desc">{t('teams.sortByUpdated')} ↓</option>
                    <option value="last_visited-asc">{t('teams.sortByUpdated')} ↑</option>
                  </Select>
                </HStack>
              )}
            </HStack>
          )}
        </VStack>
      )}

      {/* Results summary */}
      {!isLoading && (
        <HStack justify="space-between" align="center">
          <Text fontSize="sm" color={textColor}>
            {t('teams.totalTeams')}: {total}
          </Text>
          {isLoading && <Spinner size="sm" />}
        </HStack>
      )}

      {/* Teams grid */}
      {teams.length === 0 && !isLoading ? (
        <Box textAlign="center" py={12}>
          <TeamIcon />
          <Text fontSize="lg" fontWeight="medium" mt={4} mb={2}>
            {emptyMessage || t('teams.noTeamsFound')}
          </Text>
          <Text color={textColor}>
            {emptyDescription || t('teams.noTeamsDescription')}
          </Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {teams.map((team) => (
            <Card
              key={team.id}
              bg={cardBg}
              border="1px"
              borderColor={borderColor}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                borderColor: 'primary.400',
                bg: hoverBg,
                transform: 'translateY(-2px)',
                shadow: 'md',
              }}
              onClick={() => handleTeamClick(team)}
            >
              <CardHeader pb={3}>
                <HStack justify="space-between" align="start">
                  <HStack spacing={3} flex={1} minW={0}>
                    <Avatar
                      size="md"
                      name={team.name}
                      src={getAvatarUrl(team)}
                      bg="primary.500"
                      color="white"
                    />
                    <VStack align="start" spacing={1} flex={1} minW={0}>
                      <Text
                        fontWeight="semibold"
                        fontSize="md"
                        color="text.primary"
                        noOfLines={1}
                      >
                        {team.name}
                      </Text>
                      <HStack spacing={2}>
                        {team.team_type && (
                          <Badge
                            colorScheme={getTeamTypeBadgeColor(team.team_type)}
                            variant="subtle"
                            size="sm"
                          >
                            {t(`teams.teamType${team.team_type.charAt(0).toUpperCase() + team.team_type.slice(1)}`)}
                          </Badge>
                        )}
                        {team.status && (
                          <Badge
                            colorScheme={getStatusBadgeColor(team.status)}
                            variant="outline"
                            size="sm"
                          >
                            {t(`teams.status${team.status.charAt(0).toUpperCase() + team.status.slice(1)}`)}
                          </Badge>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>

                  <Menu>
                    <MenuButton
                      as={IconButton}
                      icon={<MoreIcon />}
                      variant="ghost"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <MenuList>
                      <MenuItem onClick={(e) => {
                        e.stopPropagation();
                        handleTeamClick(team);
                      }}>
                        {t('teams.viewDetails')}
                      </MenuItem>
                      {listType === 'all' && (
                        <MenuItem onClick={(e) => {
                          e.stopPropagation();
                          // Handle join team
                        }}>
                          {t('teams.requestMembership')}
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                </HStack>
              </CardHeader>

              <CardBody pt={0}>
                <VStack align="stretch" spacing={3}>
                  {team.description && (
                    <Text
                      color={textColor}
                      fontSize="sm"
                      noOfLines={3}
                      lineHeight="1.4"
                    >
                      {team.description}
                    </Text>
                  )}

                  <Divider />

                  <HStack justify="space-between" fontSize="xs" color={textColor}>
                    <Text>
                      {t('teams.created')}: {team.created_at ? new Date(team.created_at).toLocaleDateString() : '-'}
                    </Text>
                    {/* {team.members_count && (
                      <Text>
                        {team.members_count} {t('teams.members')}
                      </Text>
                    )} */}
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Pagination */}
      {total > pageSize && (
        <Flex justify="center" mt={6}>
          <HStack spacing={2}>
            <Button
              size="sm"
              isDisabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              {t('common.previous')}
            </Button>
            
            <Text fontSize="sm" px={4}>
              {page + 1} / {Math.ceil(total / pageSize)}
            </Text>
            
            <Button
              size="sm"
              isDisabled={(page + 1) * pageSize >= total}
              onClick={() => setPage(page + 1)}
            >
              {t('common.next')}
            </Button>
          </HStack>
        </Flex>
      )}
    </VStack>
  );
}; 